import { useState, useEffect, useRef } from "react";
import { jwtFetch } from "./utils/apiClient";
import { format } from "date-fns";
import { Tabs, Tab } from "react-bootstrap";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SalesOrder = () => {
  const [ salesOrders, setSalesOrders ] = useState(null);
  const [ salesOrderInvoices, setSalesOrderInvoices ] = useState(null);
  const [ tableFilters, setTableFilters ] = useState(null);
  const [ tableSearch, setTableSearch ] = useState('');
  const [ tableSortBy, setTableSortBy ] = useState('so_code');
  const [ tableSortType, setTableSortType ] = useState('descending');
  const [ isLoadingPage, setIsLoadingPage ] = useState(true);
  const searchInputRef = useRef(null);

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    setTableFilters({
      salesOrderStatus: 'In Progress',
      salesOrderCreatedBy: crmUser.user_level === 'User' && crmUser.user_department === 'Sales' ? [crmUser.user_keyid] : []
    });
    getSalesOrders();
    getSalesOrderInvoices();
  }, []);

  const getSalesOrders = async () => {
    try {
      const requestSalesOrders = {};
      const fetchSalesOrders = await jwtFetch(config.ENDPOINT + 'get/so', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestSalesOrders)
      });
      const responseSalesOrders = await fetchSalesOrders.json();
      if (responseSalesOrders.data) {
        for (let i in responseSalesOrders.data) {
          let searchData = String(responseSalesOrders.data[i].quotation_code ?? '').toLowerCase();
          searchData += String(responseSalesOrders.data[i].so_code ?? '').toLowerCase();
          searchData += String(responseSalesOrders.data[i].company_name ?? '').toLowerCase();
          searchData += String(responseSalesOrders.data[i].subcompany_name ?? '').toLowerCase();
          searchData += String(responseSalesOrders.data[i].item_list_names ?? '').toLowerCase();
          searchData += String(responseSalesOrders.data[i].sales_name ?? '').toLowerCase();
          responseSalesOrders.data[i].search = searchData;
        }
        setSalesOrders(responseSalesOrders.data);
        setIsLoadingPage(false);
      } else {
        alert(responseSalesOrders.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrders:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getSalesOrderInvoices = async () => {
    try {
      const requestSalesOrderInvoices = {};
      const fetchSalesOrderInvoices = await jwtFetch(config.ENDPOINT + 'get/so_invoice', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestSalesOrderInvoices)
      });
      const responseSalesOrderInvoices = await fetchSalesOrderInvoices.json();
      if (responseSalesOrderInvoices.data) {
        // Sort the data similar to MySQL ORDER BY dt_so.so_code, CAST(SUBSTRING(dt_iou.term_number, 10) AS UNSIGNED)
        const sortedData = [...responseSalesOrderInvoices.data].sort((a, b) => {
          // First sort by so_code
          if (a.so_code !== b.so_code) {
            return (a.so_code || '').localeCompare(b.so_code || '');
          }

          // Then sort by numeric part of iou_term_number (Schedule X)
          const getScheduleNumber = (termNumber) => {
            if (!termNumber) return 0;
            const match = termNumber.match(/Schedule (\d+)/);
            return match ? parseInt(match[1], 10) : 0;
          };

          const aScheduleNum = getScheduleNumber(a.iou_term_number);
          const bScheduleNum = getScheduleNumber(b.iou_term_number);

          return aScheduleNum - bScheduleNum;
        });

        setSalesOrderInvoices(sortedData);
      } else {
        alert(responseSalesOrderInvoices.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrderInvoices:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const filterTable = (key, value) => {
    let filters = {...tableFilters};
    if (key === 'status') {
      filters.salesOrderStatus = value;
    }
    if (key === 'createdBy') {
      if (filters.salesOrderCreatedBy.includes(value)) {
        filters.salesOrderCreatedBy = filters.salesOrderCreatedBy.filter(item => item !== value);
      } else {
        filters.salesOrderCreatedBy.push(value);
      }
    }
    setTableFilters(filters);
  }

  const sortTable = (sortBy) => {
    if (tableSortBy === sortBy) {
      setTableSortType(tableSortType === 'ascending' ? 'descending' : 'ascending');
    } else {
      setTableSortBy(sortBy);
      setTableSortType('ascending');
    }
  }

  const clearSearch = () => {
    setTableSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }
  
  const getStatusCount = (status) => {
    if (!salesOrders) return 0;
    // return salesOrders.filter(salesOrder => 
    //   salesOrder.so_status === status && 
    //   (tableSearch === '' || salesOrder.search.includes(tableSearch.toLowerCase()))
    // ).length;
    return salesOrders.filter(salesOrder => 
      salesOrder.so_status === status && 
      (tableSearch === '' || salesOrder.search.includes(tableSearch.toLowerCase())) &&
      (crmUser.user_level === 'User' && crmUser.user_department === 'Sales' ? 
        [crmUser.user_keyid].includes(salesOrder.created_by) : 
        true
      )
    ).length;
  }

  const generateXls = () => {
    const headers = ["#", "SO Number", "Status", "Created Date", "Quotation Number", "Invoice Request Number", "Schedule", "Company Group", "Subsidiary", "Sales Name"];
    const data = salesOrderInvoices.map((salesOrderInvoice, s) => [
      s + 1,
      salesOrderInvoice.so_code,
      salesOrderInvoice.so_status.replace('Canceled', 'Cancelled'),
      format(salesOrderInvoice.created_on, 'd MMM yyyy'),
      salesOrderInvoice.quotation_code,
      salesOrderInvoice.iou_code,
      salesOrderInvoice.iou_term_number ? salesOrderInvoice.iou_term_number.replace('Term', 'Schedule') : '',
      salesOrderInvoice.company_name,
      salesOrderInvoice.subcompany_name,
      salesOrderInvoice.sales_name,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
    saveAs(blob, "salesorder.xlsx");
  }

  return (
    <div id="salesorder-list">
      {console.log(tableFilters)}
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="salesorder"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">

                <section className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="fs-24">Sales Order List</div>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="input-group input-search">
                        <button className="btn bg-white" type="button" onClick={() => clearSearch()}>{tableSearch ? <i className="bi bi-x-lg"/> : <i className="bi bi-search"/>}</button>
                        <input className="form-control" type="text" placeholder="Search" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} ref={searchInputRef} autoFocus/>
                      </div>
                      <button className="btn btn-link bg-gold" type="button" onClick={() => generateXls()}><i className="bi bi-download me-1"/> Export</button>
                    </div>
                  </div>
                  <div className="bg-white border-radius-4 p-4">
                    <Tabs className="mb-3" defaultActiveKey="In Progress" onSelect={(key) => filterTable('status', key)}>
                      <Tab eventKey="In Progress" title={`In Progress${getStatusCount('In Progress') > 0 ? ` (${getStatusCount('In Progress')})` : ''}`} />
                      <Tab eventKey="Completed" title={`Completed${getStatusCount('Completed') > 0 ? ` (${getStatusCount('Completed')})` : ''}`} />
                      <Tab eventKey="Canceled" title={`Cancelled${getStatusCount('Canceled') > 0 ? ` (${getStatusCount('Canceled')})` : ''}`} />
                    </Tabs>
                    {salesOrders && salesOrders.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="bg-gold w-40">#</th>
                              <th className="bg-gold w-140 pointer" onClick={() => sortTable('so_code')}>Number {tableSortBy === 'so_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-105 pointer" onClick={() => sortTable('so_status')}>Label {tableSortBy === 'so_status' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('company_name')}>Company Name {tableSortBy === 'company_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-150 pointer text-start" onClick={() => sortTable('sales_name')}>Sales Name {tableSortBy === 'sales_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-125 pointer text-end" onClick={() => sortTable('so_total')}>Amount {tableSortBy === 'so_total' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2"/> : <span className="bi bi-sort-up-alt ms-2"/>)}</th>
                              <th className="bg-gold w-40"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...salesOrders]
                              .sort((a, b) => {
                                const aValue = a[tableSortBy];
                                const bValue = b[tableSortBy];
                                const aNum = Number(aValue);
                                const bNum = Number(bValue);
                                const isNumeric = !isNaN(aNum) && !isNaN(bNum);
                                if (isNumeric) {
                                  return tableSortType === 'ascending' ? aNum - bNum : bNum - aNum;
                                }
                                return tableSortType === 'ascending'
                                  ? (aValue ?? '').toString().localeCompare((bValue ?? '').toString())
                                  : (bValue ?? '').toString().localeCompare((aValue ?? '').toString());
                              })
                              .filter(salesOrder => salesOrder.search.includes(tableSearch.toLowerCase()))
                              .filter(salesOrder => {
                                if (tableFilters && tableFilters.salesOrderStatus) {
                                  return salesOrder.so_status === tableFilters.salesOrderStatus;
                                }
                                return true;
                              })
                              .filter(salesOrder => {
                                if (tableFilters) {
                                  if (tableFilters.salesOrderCreatedBy && tableFilters.salesOrderCreatedBy.length > 0) {
                                    return tableFilters.salesOrderCreatedBy.includes(salesOrder.created_by);
                                  }
                                  return true;
                                }
                                return true;
                              })
                              .map((salesOrder, s) => (
                              <tr key={"salesOrder-" + s}>
                                <td valign="middle" align="center">{s + 1}</td>
                                <td valign="middle" align="center">{salesOrder.so_code}</td>
                                <td valign="middle" align="center"><span className={"badge text-bg-" + (salesOrder.so_status === 'Completed' ? 'success' : (salesOrder.so_status === 'In Progress' ? 'secondary' : 'danger'))}>{salesOrder.so_status.replace('Canceled', 'Cancelled')}</span></td>
                                <td valign="middle">
                                  <strong>{salesOrder.company_name}</strong>
                                  <br/>
                                  <small>{salesOrder.subcompany_billing_name}</small>
                                </td>
                                <td valign="middle">
                                  {salesOrder.sales_name}
                                  <br/>
                                  <small>Created on {format(salesOrder.created_on, 'd MMM yyyy')}</small>
                                </td>
                                <td valign="middle" align="right">{priceFormat(salesOrder.so_total, salesOrder.so_currency)}</td>
                                <td valign="middle" align="right"><a className="btn btn-sm bg-gold" href={'/salesorder/detail/' + salesOrder.id_so}><i className="bi bi-eye"/></a></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>{isLoadingPage ? 'Loading...' : 'No data'}</p>
                    )}
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesOrder;

